import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Check, Information } from '@/components/Icon';
import { Text } from '@/components/UI/Typography/Text';
import { Box, Flex } from '@/layout';
import { theme } from '@/theme/Theme';
import { formatPblDisplayDate } from '@/utils/pblDate';
import { translatePblDisplayList, translatePblDisplayValue } from '@/utils/pblDisplayTranslation';

import { FormSectionTitle } from './styles';
import type { VolunteerFormState, VolunteerUploadedDocument } from './types';

type ReviewSummaryContentProps = {
  form: VolunteerFormState;
  serviceAreaLabels: string[];
  onEditStep?: (stepIndex: number) => void;
  showHeader?: boolean;
  showEditLinks?: boolean;
  showSubmitNotice?: boolean;
  hideSectionsFromAuthorisation?: boolean;
  firmAuthorisationDocument?: VolunteerUploadedDocument | null;
  firmAuthorisationFileName?: string | null;
  conductSupportingDocuments?: VolunteerUploadedDocument[] | null;
  conductSupportingDocumentNames?: string[] | null;
};

const Root = styled.div`
  width: 100%;
`;

const Section = styled.div<{ first?: boolean }>`
  border-top: ${(p) => (p.first ? '0' : `1px solid ${p.theme.colors.gray100}`)};
  padding-top: ${(p) => (p.first ? '0' : '16px')};
  margin-top: ${(p) => (p.first ? '0' : '16px')};
`;

const Rows = styled.div`
  display: grid;
  grid-template-columns: minmax(210px, 32%) minmax(0, 1fr);
  gap: 10px 28px;
  margin-top: 12px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 4px;
  }
`;

const Label = styled(Text)`
  margin: 0;
  color: ${(p) => p.theme.colors.gray300};
  line-height: 1.25;
`;

const Value = styled(Text)`
  margin: 0;
  color: ${(p) => p.theme.colors.gray500};
  line-height: 1.35;
  overflow-wrap: anywhere;
`;

const EditLink = styled.button`
  display: inline-block;
  margin-top: 10px;
  color: ${(p) => p.theme.colors.primary500};
  text-decoration: underline;
  cursor: pointer;
`;

const DocumentLink = styled.a`
  display: inline-block;
  margin: 4px 0;
  color: ${(p) => p.theme.colors.primary500};
  text-decoration: underline;
`;

const ConsentRow = styled(Flex)`
  margin: 10px 0;
`;

const Notice = styled(Flex)`
  margin-top: 24px;
  padding: 14px 16px;
  border: 1px solid #c7dffb;
  border-radius: ${(p) => p.theme.radii.base};
  background: #edf5fe;
`;

const valueOrDash = (value?: string | number | null) =>
  value === undefined || value === null || value === '' ? '-' : value;
const dateOrDash = (value?: string | number | null) => formatPblDisplayDate(value);

const ReviewRow = ({ label, value }: { label: string; value?: string | number | null }) => (
  <>
    <Label>{label}</Label>
    <Value>{valueOrDash(value)}</Value>
  </>
);

const CheckedText = ({ children }: { children: React.ReactNode }) => (
  <ConsentRow alignItems="flex-start" gridGap={8}>
    <Box flexShrink={0} marginTop="2px">
      <Check.CheckSVG width={22} height={22} fill={theme.colors.secondary400} />
    </Box>
    <Value>{children}</Value>
  </ConsentRow>
);

const MaybeEdit = ({
  show,
  stepIndex,
  onEditStep,
  label,
}: {
  show: boolean;
  stepIndex: number;
  onEditStep?: (stepIndex: number) => void;
  label: string;
}) =>
  show && onEditStep ? (
    <EditLink type="button" onClick={() => onEditStep(stepIndex)}>
      {label}
    </EditLink>
  ) : null;

type DocumentItem = {
  name: string;
  href?: string;
};

const toDocumentItem = (document?: VolunteerUploadedDocument | null): DocumentItem | null =>
  document?.name ? { name: document.name, href: document.url } : null;

const namedDocuments = (documentNames?: string[] | null): DocumentItem[] =>
  (documentNames || []).filter(Boolean).map((name) => ({ name }));

const UploadedDocumentLink = ({ document }: { document: DocumentItem }) => (
  <DocumentLink
    href={document.href || '#'}
    download={document.href ? document.name : undefined}
    onClick={document.href ? undefined : (event) => event.preventDefault()}
  >
    {document.name}
  </DocumentLink>
);

const ReviewSummaryContent = ({
  form,
  serviceAreaLabels,
  onEditStep,
  showHeader = true,
  showEditLinks = true,
  showSubmitNotice = true,
  hideSectionsFromAuthorisation = false,
  firmAuthorisationDocument,
  firmAuthorisationFileName,
  conductSupportingDocuments,
  conductSupportingDocumentNames,
}: ReviewSummaryContentProps) => {
  //   @ts-ignore
  const { t } = useTranslation();
  const tr = (key: string, defaultValue: string) => t(key as any, { defaultValue }) as string;
  const translatedServiceAreaLabels = serviceAreaLabels.map((label) => translatePblDisplayValue(t, label));
  const rawServiceAreaText = serviceAreaLabels.join(', ');
  const serviceAreaText = translatedServiceAreaLabels.join(', ');
  const editLabel = tr('pbl.common.actions.edit', 'Edit');
  const yesNo = (value?: boolean) => (value ? tr('pbl.common.yes', 'Yes') : tr('pbl.common.no', 'No'));
  const reviewSection = (key: string, defaultValue: string) =>
    tr(`pbl.lawyerRegistration.reviewSubmit.sections.${key}`, defaultValue);
  const reviewLabel = (key: string, defaultValue: string) =>
    tr(`pbl.lawyerRegistration.reviewSubmit.labels.${key}`, defaultValue);
  const firmAuthorisationDocumentItem =
    toDocumentItem(form.firm_authorisation_file) ||
    toDocumentItem(firmAuthorisationDocument) ||
    (firmAuthorisationFileName ? { name: firmAuthorisationFileName } : null);
  const backendConductDocuments = (conductSupportingDocuments || [])
    .map((document) => toDocumentItem(document))
    .filter((document): document is DocumentItem => Boolean(document));
  const conductSupportingDocumentItems = toDocumentItem(form.conduct_supporting_document_file)
    ? [toDocumentItem(form.conduct_supporting_document_file) as DocumentItem]
    : backendConductDocuments.length > 0
    ? backendConductDocuments
    : namedDocuments(conductSupportingDocumentNames);

  return (
    <Root>
      {showHeader && (
        <FormSectionTitle>
          {t('pbl.lawyerRegistration.reviewSubmit.title', { defaultValue: 'Review & Submit' })}
        </FormSectionTitle>
      )}

      <Section first>
        <FormSectionTitle>{reviewSection('personalInformation', 'Personal Information')}</FormSectionTitle>
        <Rows>
          <ReviewRow label={reviewLabel('fullNameEn', 'Full Name (English)')} value={form.full_name_en} />
          <ReviewRow label={reviewLabel('fullNameZh', 'Full Name (Chinese)')} value={form.full_name_zh} />
          <ReviewRow label={reviewLabel('phone', 'Phone Number')} value={form.phone} />
          <ReviewRow label={reviewLabel('email', 'Email Address')} value={form.email} />
          <ReviewRow
            label={reviewLabel('admissionYear', 'Year of Admission as a Hong Kong Solicitor')}
            value={form.hk_solicitor_admission_year}
          />
          <ReviewRow
            label={reviewLabel('otherProfessionalQualifications', 'Other Professional Qualifications')}
            value={translatePblDisplayList(t, form.cv_other_qualifications_en)}
          />
          <ReviewRow
            label={reviewLabel('languageProficiency', 'Language Proficiency')}
            value={translatePblDisplayList(t, form.cv_languages_en)}
          />
        </Rows>
        <MaybeEdit show={showEditLinks} stepIndex={0} onEditStep={onEditStep} label={editLabel} />
      </Section>

      <Section>
        <FormSectionTitle>{reviewSection('practiceInformation', 'Practice Information')}</FormSectionTitle>
        <Rows>
          <ReviewRow label={reviewLabel('lawFirmName', 'Law Firm Name')} value={form.law_firm_name} />
          <ReviewRow label={reviewLabel('lawFirmNameZh', 'Law Firm Name (Chinese)')} value={form.law_firm_name_zh} />
          <ReviewRow
            label={reviewLabel('position', 'Your Position in the Law Firm')}
            value={translatePblDisplayValue(t, form.cv_position_en || form.position_in_law_firm)}
          />
        </Rows>
        <MaybeEdit show={showEditLinks} stepIndex={1} onEditStep={onEditStep} label={editLabel} />
      </Section>

      <Section>
        <FormSectionTitle>{reviewSection('serviceAreas', 'Pro Bono Legal Service Areas')}</FormSectionTitle>
        <Rows>
          <ReviewRow label={reviewLabel('selectApplicableAreas', 'Select applicable areas')} value={serviceAreaText} />
        </Rows>
        <MaybeEdit show={showEditLinks} stepIndex={2} onEditStep={onEditStep} label={editLabel} />
      </Section>

      <Section>
        <FormSectionTitle>{reviewSection('profileCv', 'Profile & CV')}</FormSectionTitle>
        <CheckedText>{reviewLabel('publicationConsent', 'I consent to the publication of my CV.')}</CheckedText>
        <Rows>
          <ReviewRow label={reviewLabel('fullNameEn', 'Full Name (English)')} value={form.cv_full_name_en} />
          <ReviewRow label={reviewLabel('fullNameZh', 'Full Name (Chinese)')} value={form.cv_full_name_zh} />
          <ReviewRow label={reviewLabel('affiliatedLawFirm', 'Affiliated Law Firm')} value={form.cv_law_firm_en} />
          <ReviewRow
            label={reviewLabel('position', 'Your Position in the Law Firm')}
            value={form.cv_position_en}
          />
          <ReviewRow
            label={t('pbl.lawyerRegistration.profileCv.fields.positionZh', {
              defaultValue: 'Position (Chinese) *',
            })}
            value={form.cv_position_zh}
          />
          <ReviewRow
            label={reviewLabel('admissionYear', 'Year of Admission as a Hong Kong Solicitor')}
            value={form.cv_admission_year_en}
          />
          <ReviewRow
            label={t('pbl.lawyerRegistration.profileCv.fields.admissionYearZh', {
              defaultValue: 'Year of Admission as Hong Kong Solicitor (Chinese) *',
            })}
            value={form.cv_admission_year_zh}
          />
          <ReviewRow
            label={reviewLabel('otherQualifications', 'Other Qualifications / Certifications')}
            value={form.cv_other_qualifications_en}
          />
          <ReviewRow
            label={t('pbl.lawyerRegistration.profileCv.fields.otherQualificationsZh', {
              defaultValue: 'Other Qualifications / Certifications (Chinese) *',
            })}
            value={form.cv_other_qualifications_zh}
          />
          <ReviewRow label={reviewLabel('language', 'Language')} value={form.cv_languages_en} />
          <ReviewRow
            label={t('pbl.lawyerRegistration.profileCv.fields.languageZh', {
              defaultValue: 'Language (Chinese) *',
            })}
            value={form.cv_languages_zh}
          />
          <ReviewRow
            label={reviewLabel('primaryPracticeAreas', 'Primary Practice Areas')}
            value={form.cv_primary_practice_areas_en || rawServiceAreaText}
          />
          <ReviewRow
            label={t('pbl.lawyerRegistration.profileCv.fields.primaryPracticeAreasZh', {
              defaultValue: 'Primary Practice Areas (Chinese) *',
            })}
            value={form.cv_primary_practice_areas_zh || serviceAreaText}
          />
          <ReviewRow
            label={reviewLabel(
              'professionalExperience',
              'Professional Experience Highlights - Brief description of core experience in the above practice areas'
            )}
            value={form.cv_professional_experience_en}
          />
          <ReviewRow
            label={reviewLabel('professionalExperienceZh', 'Professional Experience Highlights (Chinese)')}
            value={form.cv_professional_experience_zh}
          />
          <ReviewRow
            label={reviewLabel(
              'proBonoExperience',
              'Pro Bono Service Experience Brief description of past pro bono / community legal service experience'
            )}
            value={form.cv_pro_bono_experience_en}
          />
          <ReviewRow
            label={reviewLabel('proBonoExperienceZh', 'Pro Bono Service Experience (Chinese)')}
            value={form.cv_pro_bono_experience_zh}
          />
        </Rows>
        <MaybeEdit show={showEditLinks} stepIndex={3} onEditStep={onEditStep} label={editLabel} />
      </Section>

      {!hideSectionsFromAuthorisation && (
        <>
          <Section>
            <FormSectionTitle>{reviewSection('authorisation', 'Authorisation from Law Firm')}</FormSectionTitle>
            {firmAuthorisationDocumentItem && <UploadedDocumentLink document={firmAuthorisationDocumentItem} />}
            <CheckedText>
              {t('pbl.lawyerRegistration.authorisation.confirmAuthorisation', {
                defaultValue:
                  'I confirm that I have authorisation from my law firm, i.e. the law firm stated in paragraph 7 above, that I am joining FreelegalLink to provide free legal advisory service to the public in my capacity in the firm as stated in paragraph 8 above.',
              })}
            </CheckedText>
            <CheckedText>
              {t('pbl.lawyerRegistration.authorisation.confirmLetterAttached', {
                defaultValue:
                  'I attach the uploaded authorisation letter (in the form prescribed in Annex 1) signed by a partner of my firm.',
              })}
            </CheckedText>
            <CheckedText>
              {t('pbl.lawyerRegistration.authorisation.agreeNotifyChange', {
                defaultValue:
                  'I agree to inform FreelegalLink immediately upon any change of law firm from the one stated in paragraph 7 above.',
              })}
            </CheckedText>
            <MaybeEdit show={showEditLinks} stepIndex={4} onEditStep={onEditStep} label={editLabel} />
          </Section>

          <Section>
            <FormSectionTitle>{reviewSection('servicePledge', 'Service Pledge')}</FormSectionTitle>
            <CheckedText>
              {t('pbl.lawyerRegistration.servicePledge.agreement', {
                defaultValue:
                  'I confirm my agreement to the use of DMP and the service pledge as set out in paragraphs 14(a), 14(b) and 14(c) above.',
              })}
            </CheckedText>
            <MaybeEdit show={showEditLinks} stepIndex={5} onEditStep={onEditStep} label={editLabel} />
          </Section>

          <Section>
            <FormSectionTitle>{reviewSection('postCompletion', 'Post Completion of Pro Bono Service')}</FormSectionTitle>
            <CheckedText>
              {t('pbl.lawyerRegistration.postCompletion.agreement', {
                defaultValue:
                  'I confirm my agreement to the submission of a completed notification and evaluation form and to the offer of service at a discounted rate and the use of the DMP for non pro bono service as set out in paragraphs 15 and 16 above.',
              })}
            </CheckedText>
            <MaybeEdit show={showEditLinks} stepIndex={6} onEditStep={onEditStep} label={editLabel} />
          </Section>

          <Section>
            <FormSectionTitle>
              {reviewSection('personalInformationCollection', 'Personal Information Collection Statement')}
            </FormSectionTitle>
            <CheckedText>
              {t('pbl.lawyerRegistration.personalInformationCollection.agreement', {
                defaultValue: 'I acknowledge and agree to the above personal data collection statement.',
              })}
            </CheckedText>
            <MaybeEdit show={showEditLinks} stepIndex={7} onEditStep={onEditStep} label={editLabel} />
          </Section>

          <Section>
            <FormSectionTitle>
              {t('pbl.lawyerRegistration.conductCheck.title', { defaultValue: 'Conduct Check' })}
            </FormSectionTitle>
            <Flex alignItems="center" gridGap={8} marginBottom="10px">
              <Label style={{ flex: 1 }}>
                {t('pbl.lawyerRegistration.conductCheck.questions.bankruptOrInsolvent', {
                  defaultValue:
                    '(1) Have you ever been made bankrupt or insolvent, or are any such proceedings pending against you in any jurisdiction?',
                })}
              </Label>
              <Value style={{ flexShrink: 0 }}>{yesNo(form.bankrupt_or_insolvent)}</Value>
            </Flex>
            <Flex alignItems="center" gridGap={8} marginBottom="10px">
              <Label style={{ flex: 1 }}>
                {t('pbl.lawyerRegistration.conductCheck.questions.arrangementWithCreditors', {
                  defaultValue: '(2) Have you ever entered into an arrangement or composition with creditors?',
                })}
              </Label>
              <Value style={{ flexShrink: 0 }}>{yesNo(form.arrangement_with_creditors)}</Value>
            </Flex>
            <Flex alignItems="center" gridGap={8} marginBottom="10px">
              <Label style={{ flex: 1 }}>
                {t('pbl.lawyerRegistration.conductCheck.questions.criminalConviction', {
                  defaultValue:
                    '(3) Have you been convicted of any offence in any court of Hong Kong or elsewhere (other than a motoring offence not resulting in disqualification)?',
                })}
              </Label>
              <Value style={{ flexShrink: 0 }}>{yesNo(form.criminal_conviction)}</Value>
            </Flex>
            <Flex alignItems="center" gridGap={8} marginBottom="10px">
              <Label style={{ flex: 1 }}>
                {t('pbl.lawyerRegistration.conductCheck.questions.professionalMisconduct', {
                  defaultValue:
                    '(4) Have you at any time been found guilty of professional misconduct by a disciplinary tribunal, or are any proceedings before a disciplinary tribunal still pending in any jurisdiction?',
                })}
              </Label>
              <Value style={{ flexShrink: 0 }}>{yesNo(form.professional_misconduct_or_pending_disciplinary)}</Value>
            </Flex>
            <Flex alignItems="center" gridGap={8} marginBottom="10px">
              <Label style={{ flex: 1 }}>
                {t('pbl.lawyerRegistration.conductCheck.questions.suspendedFromPractice', {
                  defaultValue:
                    '(5) Have you at any time been suspended from professional practice by your professional body?',
                })}
              </Label>
              <Value style={{ flexShrink: 0 }}>{yesNo(form.suspended_from_professional_practice)}</Value>
            </Flex>
            <Flex flexDirection="column">
              {conductSupportingDocumentItems.map((document) => (
                <UploadedDocumentLink key={document.name} document={document} />
              ))}
            </Flex>
            <MaybeEdit show={showEditLinks} stepIndex={8} onEditStep={onEditStep} label={editLabel} />
          </Section>

          <Section>
            <FormSectionTitle>{reviewSection('signature', 'Signature')}</FormSectionTitle>
            <CheckedText>
              <span>
                {reviewLabel('applicantSignature', "Applicant's Signature")}
                <br />
                <Label as="span">
                  {reviewLabel('date', 'Date:')} {dateOrDash(form.declaration_signed_at)}
                </Label>
              </span>
            </CheckedText>
            <MaybeEdit show={showEditLinks} stepIndex={9} onEditStep={onEditStep} label={editLabel} />
          </Section>
        </>
      )}

      {showSubmitNotice && (
        <Notice alignItems="center" gridGap={10}>
          <Information.InformationSVG />
          <Value>
            {t('pbl.lawyerRegistration.reviewSubmit.notice', {
              defaultValue: 'Please review all details before submission',
            })}
          </Value>
        </Notice>
      )}
    </Root>
  );
};

export default ReviewSummaryContent;
